import { EventType } from "@/analytics/analytics-events";
import { EditElementProperties } from "@/analytics/data-organization-events-properties";
import { ElementIconType, TYPE_TO_ICON_MAP } from "@/components/ui/icons";
import { TagsMenu } from "@/components/ui/tags/tags-menu";
import { useAssignTagsToIElement } from "@/components/ui/tags/use-assign-tags-to-ielement";
import { createStaticProjectTagOptions } from "@/components/ui/tags/use-project-tag-options";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectTags } from "@/store/tags/tags-selectors";
import { selectShowSpinner } from "@/store/ui/ui-selectors";
import { setShowSpinner } from "@/store/ui/ui-slice";
import { useNonExhaustiveEffect } from "@faro-lotv/app-component-toolbox";
import {
  blue,
  FaroButton,
  FaroMenuItem,
  FilterMenuOptionType,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { selectIElement } from "@faro-lotv/project-source";
import {
  ComponentProps,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { ContextMenuAction, ContextMenuActionType } from "../action-types";
import { ContextMenuItemLabel } from "../context-menu-action-item";

type AssignTagsComponentProps = ComponentProps<
  NonNullable<ContextMenuAction["CustomComponent"]>
>;

type AssignTagsCommonProps = Pick<AssignTagsComponentProps, "dark"> & {
  /** The label to show for the action item */
  label?: string;
  /** The icon to show for the action item */
  icon?: ElementIconType;
};

type AssignTagsMenuItemProps = AssignTagsCommonProps & {
  /** Callback when the menu item is clicked */
  onClick?(): void;
};

const AssignTagsMenuItem = forwardRef<HTMLDivElement, AssignTagsMenuItemProps>(
  function AssignTagsMenuItem(
    { label, icon, dark, onClick },
    ref,
  ): JSX.Element {
    return (
      <FaroMenuItem
        dark={dark}
        label={<ContextMenuItemLabel ref={ref} label={label} />}
        aria-label={label}
        Icon={icon ? TYPE_TO_ICON_MAP[icon] : undefined}
        onClick={(ev) => {
          // Prevent the label from receiving the event and selecting the element.
          ev.stopPropagation();
          onClick?.();
        }}
        sx={{
          mx: 1,
          px: 1,
          color: blue[500],
        }}
      />
    );
  },
);

type AssignTagsActionContentProps = AssignTagsCommonProps &
  Pick<AssignTagsComponentProps, "elementId" | "closeContextMenu"> & {
    /** Callback after tags have been changed successfully */
    onTagsChanged?(): void;
  };

/**
 *  @returns the content for the "Assign Tags" context menu action.
 */
export function AssignTagsActionContent({
  elementId,
  label,
  icon,
  dark,
  onTagsChanged,
  closeContextMenu,
}: AssignTagsActionContentProps): JSX.Element | null {
  const element = useAppSelector(selectIElement(elementId));

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const actionItemRef = useRef<HTMLDivElement | null>(null);

  const projectTags = useAppSelector(selectTags);
  const tagOptions = useMemo(
    () => createStaticProjectTagOptions(projectTags),
    [projectTags],
  );

  const [selectedTagOptions, setSelectedTagOptions] = useState<
    FilterMenuOptionType[]
  >(() => []);

  const currentTags = element?.labels;

  // Initialize the list of selected tags, any time the menu is opened.
  useNonExhaustiveEffect(() => {
    setSelectedTagOptions(
      tagOptions.filter((option) =>
        currentTags?.some((tag) => tag.id === option.key),
      ),
    );
  }, [isMenuOpen]);

  // Make sure deleted tags are removed from the selection.
  useNonExhaustiveEffect(() => {
    setSelectedTagOptions((prevSelectedOptions) =>
      prevSelectedOptions.filter((selectedOption) =>
        tagOptions.some((option) => option.key === selectedOption.key),
      ),
    );
  }, [projectTags]);

  const isLoading = useAppSelector(selectShowSpinner);

  const mapOptionToTag = useCallback(
    (option: FilterMenuOptionType) => {
      const tag = projectTags.find((tag) => tag.id === option.key);
      assert(tag, "Selected tag option not found in labels");
      return tag;
    },
    [projectTags],
  );

  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorHandlers();

  const assignTagsToIElement = useAssignTagsToIElement();

  const assignTags = useCallback(async () => {
    dispatch(setShowSpinner(true));
    try {
      if (!element) throw new Error("No element to assign tags to");

      Analytics.track<EditElementProperties>(EventType.editElement, {
        via: "capture tree context menu",
        elementType: element.type,
        elementTypeHint: element.typeHint ?? undefined,
        hasEditedName: false,
        hasEditedTimePoint: false,
        hasEditedTags: true,
        hasEditedColor: false,
      });

      const newSelectedTags = selectedTagOptions.map(mapOptionToTag);

      await assignTagsToIElement(element, newSelectedTags, tagOptions);
      closeContextMenu();
      onTagsChanged?.();
    } catch (error) {
      handleErrorWithToast({ title: "Could not assign tags", error });
    } finally {
      dispatch(setShowSpinner(false));
      setIsMenuOpen(false);
    }
  }, [
    assignTagsToIElement,
    closeContextMenu,
    dispatch,
    element,
    handleErrorWithToast,
    mapOptionToTag,
    onTagsChanged,
    selectedTagOptions,
    tagOptions,
  ]);

  if (!element) return null;
  return (
    <>
      <AssignTagsMenuItem
        ref={actionItemRef}
        label={label}
        icon={icon}
        dark={dark}
        onClick={() => setIsMenuOpen(true)}
      />
      {isMenuOpen && (
        <TagsMenu
          title="Assign tags"
          isOpen
          anchorEl={actionItemRef.current}
          onClose={() => setIsMenuOpen(false)}
          options={tagOptions}
          value={selectedTagOptions}
          onChange={setSelectedTagOptions}
          disabled={isLoading}
          anchorOrigin={{ horizontal: "right", vertical: "top" }}
          dark={dark}
        >
          <FaroButton
            onClick={assignTags}
            isLoading={isLoading}
            size="m"
            dark={dark}
          >
            Assign
          </FaroButton>
        </TagsMenu>
      )}
    </>
  );
}

/**
 * This component is necessary to map the action props to the AssignTagsActionContent props.
 *
 * @returns a component for the "Assign Tags" context menu action.
 * @param action The ContextMenuAction object for this menu item. This is used to render the menu item itself
 *   via ContextMenuActionItem, ensuring consistent style and behavior. The action contains metadata such as
 *   label, icon, and type, which are required for accessibility and rendering.
 * @param elementId The ID of the element to assign tags to.
 * @param dark Whether to use dark mode styling.
 * @param closeContextMenu Callback to close the context menu.
 */
function AssignTagsComponent({
  elementId,
  action,
  dark,
  closeContextMenu,
}: AssignTagsComponentProps): JSX.Element | null {
  return (
    <AssignTagsActionContent
      elementId={elementId}
      label={action.label}
      icon={action.icon}
      dark={dark}
      closeContextMenu={closeContextMenu}
    />
  );
}

// This action's custom component uses ContextMenuActionItem internally,
// so label and icon are still defined here for accessibility purposes.
// This is done so that the style and behavior of the action item remains consistent, while allowing for custom content.
export const ASSIGN_TAGS_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.assignTags,
  label: "Assign tag",
  icon: ElementIconType.TagsAddIcon,
  CustomComponent: AssignTagsComponent,
};
